import {
  Box,
  IconButton,
  CircularProgress,
  Typography,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { useContext, useEffect, useMemo, useCallback, useState } from "react";
import LoggedInLayout from "../../layout/LoggedInLayout";
import PersonalityCircle from "../CareerNewPdf/PersonalityResult/PersonalityCircle";
import ProgressBar from "../CareerNewPdf/PersonalityResult/ProgressBar";
import httpcommon from "../../helper/httpcommon";
import AuthContext from "../../context/AuthContext";
import { AssignmentInd, Search } from "@mui/icons-material";
import RecommendedCareer from "./RecommendedCareer";
import { useNavigate } from "react-router-dom";
import JourenyStepper from "./JourenyStepper";
import CommonTextField from "../common/CommonTextField";
import { fetchParentFeedbackStatus, fetchParentStudentLinks } from "../../services/api";

const SELF_DASHBOARD_VALUE = "self";
const PARENT_DASHBOARD_SELECTION_STORAGE_KEY = "parent-portal-dashboard-selection";
const PARENT_ASSESSMENT_CHILD_STORAGE_KEY = "parent-portal-selected-child";

/* -------------------- Utils -------------------- */
const getColorByValue = (value = 0) => {
  if (value >= 8)
    return "linear-gradient(90deg, #B9F6D1 0%, #31C574 100%)";

  if (value >= 7)
    return "linear-gradient(90deg, #E3E3E3 0%, #FFFFFF 100%)";

  if (value >= 6)
    return "linear-gradient(90deg, #C6C5FF 0%, #5D5BFA 100%)";

  if (value >= 5)
    return "linear-gradient(90deg, #FFE2B0 0%, #FCAF2B 100%)";

  if (value >= 4)
    return "linear-gradient(90deg, #C8F7B5 0%, #2EEE00 100%)";

  return "linear-gradient(90deg, #FFB1A4 0%, #F86300 100%)";
};


/* -------------------- Styles -------------------- */
const styles = {
  wrapper: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
    justifyContent: "center",
    padding: { xs: "10px", sm: "10px 15px 20px 10px" },
  },
  headerRow: {
    display: "flex",
    gap: 1,
    justifyContent: "start",
    mt: { xs: 2, sm: 4 },
    mb: 2,
    ml: { xs: 1, sm: 2 },
    flexWrap: "wrap",
  },
  headerBox: {
    width: "fit-content",
    maxWidth: "100%",
    minHeight: 45,
    backgroundColor: "rgba(243,243,245,1)",
    padding: "10px 15px",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    borderRadius: "10px",
    fontSize: { xs: "14px", sm: "16px" },
    fontWeight: 600,
    gap: 2,
  },
  personalitySection: {
    display: "flex",
    gap: 1,
    justifyContent: { xs: "center", md: "flex-start" },
    mt: { xs: 2, sm: 4 },
    mb: { xs: 4, sm: 8 },
    ml: { xs: 0, md: "8%" },
    flexWrap: "wrap",
  },
  milestoneCard: {
    mb: { xs: 4, sm: 8 },
    p: { xs: 2, sm: 4 },
    ml: { xs: 0, sm: "20px" },
    borderRadius: "16px",
    backgroundColor: "#F5F6F8",
    display: "flex",
    justifyContent: "space-evenly",
    gap: 2,
    flexDirection: {
      xs: "column",
      md: "row",
    },
  },
  milestoneLeft: {
    maxWidth: "200px",
  },
  titleText: {
    fontSize: "17px",
    fontWeight: 400,
    color: "#231F20",
    mb: 1,
  },
  descriptionText: {
    fontSize: "11px",
    color: "#6B7280",
    mb: 2,
    fontWeight: 400,
  },
  orangeButton: {
    display: "inline-block",
    backgroundColor: "#F6AA48",
    color: "#fff",
    fontSize: "12px",
    px: 2,
    py: 0.7,
    borderRadius: "6px",
    cursor: "pointer",
  },
  circleImageWrapper: {
    width: 168,
    height: 168,
    borderRadius: "50%",
    border: "6px solid #F6AA48",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fff",
  },
  circleImage: {
    width: "168px",
    height: "168px",
    borderRadius: "50%",
    objectFit: "cover",
  },
  milestoneRight: {
    minWidth: "200px",
  },
  progressBadge: {
    display: "inline-block",
    backgroundColor: "#F15A29",
    color: "#fff",
    fontSize: "12px",
    px: 2,
    py: 0.7,
    borderRadius: "6px",
  },
  progressSection: {
    ml: { xs: 0, md: "30px" },
    width: "100%",
    overflowX: "hidden",
  },
};

/* -------------------- Component -------------------- */
const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const isParentPortal = user?.role === "Parent";

  const userId = user?.userInfo?.user;
  const parentDisplayName =
    user?.userInfo?.user_name || user?.userInfo?.name || "Parent";
  const navigate = useNavigate();

  const [careerPdfData, setCareerPdfData] = useState(null);
  const [careerInfoData, setCareerInfoData] = useState({});

  const [loading, setLoading] = useState(false);
  const [linkedChildren, setLinkedChildren] = useState([]);
  const [selectedDashboardUserId, setSelectedDashboardUserId] = useState(
    isParentPortal ? SELF_DASHBOARD_VALUE : "",
  );
  const [childrenLoading, setChildrenLoading] = useState(false);
  const [parentFeedbackStatus, setParentFeedbackStatus] = useState(null);
  const [parentFeedbackLoading, setParentFeedbackLoading] = useState(false);

  const selectedChild = useMemo(
    () =>
      linkedChildren.find(
        (child) => String(child.id) === String(selectedDashboardUserId),
      ) || null,
    [linkedChildren, selectedDashboardUserId],
  );
  const isParentSelfSelected =
    isParentPortal && selectedDashboardUserId === SELF_DASHBOARD_VALUE;

  useEffect(() => {
    if (!isParentPortal) {
      setLinkedChildren([]);
      setSelectedDashboardUserId("");
      return;
    }

    let isMounted = true;

    const loadLinkedChildren = async () => {
      setChildrenLoading(true);
      try {
        const response = await fetchParentStudentLinks();
        const links = response?.data?.links || response?.data?.data?.links || [];
        const acceptedChildren = links
          .filter((link) => link.status === "accepted" && link.student)
          .map((link) => link.student)
          .filter(
            (child, index, children) =>
              child?.id && children.findIndex((item) => item.id === child.id) === index,
          );

        if (!isMounted) return;

        setLinkedChildren(acceptedChildren);

        const storedSelection = localStorage.getItem(
          PARENT_DASHBOARD_SELECTION_STORAGE_KEY,
        );
        const storedChildExists = acceptedChildren.some(
          (child) => String(child.id) === storedSelection,
        );
        const nextSelection =
          storedSelection === SELF_DASHBOARD_VALUE || storedChildExists
            ? storedSelection
            : SELF_DASHBOARD_VALUE;

        setSelectedDashboardUserId(nextSelection);
        localStorage.setItem(
          PARENT_DASHBOARD_SELECTION_STORAGE_KEY,
          nextSelection,
        );
      } catch {
        if (isMounted) {
          setLinkedChildren([]);
          setSelectedDashboardUserId(SELF_DASHBOARD_VALUE);
          localStorage.setItem(
            PARENT_DASHBOARD_SELECTION_STORAGE_KEY,
            SELF_DASHBOARD_VALUE,
          );
        }
      } finally {
        if (isMounted) {
          setChildrenLoading(false);
        }
      }
    };

    loadLinkedChildren();

    return () => {
      isMounted = false;
    };
  }, [isParentPortal]);

  const handleDashboardSelectionChange = (event) => {
    const nextSelection = event.target.value;
    setSelectedDashboardUserId(nextSelection);
    localStorage.setItem(
      PARENT_DASHBOARD_SELECTION_STORAGE_KEY,
      nextSelection,
    );
  };

  const openParentAssessment = () => {
    if (selectedChild?.id) {
      localStorage.setItem(
        PARENT_ASSESSMENT_CHILD_STORAGE_KEY,
        String(selectedChild.id),
      );
    }
    navigate("/parent-portal/assessment");
  };

  useEffect(() => {
    if (!isParentPortal || !selectedChild) {
      setParentFeedbackStatus(null);
      setParentFeedbackLoading(false);
      return;
    }

    let isMounted = true;

    const loadParentFeedbackStatus = async () => {
      try {
        setParentFeedbackLoading(true);
        const response = await fetchParentFeedbackStatus(selectedChild.id);
        if (isMounted) {
          setParentFeedbackStatus(response?.data || null);
        }
      } catch {
        if (isMounted) {
          setParentFeedbackStatus(null);
        }
      } finally {
        if (isMounted) {
          setParentFeedbackLoading(false);
        }
      }
    };

    loadParentFeedbackStatus();

    return () => {
      isMounted = false;
    };
  }, [isParentPortal, selectedChild]);

  const fetchDashboardData = useCallback(async () => {
    if (isParentPortal) {
      setCareerPdfData(null);
      setCareerInfoData({});

      if (!isParentSelfSelected && !selectedChild?.student_id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const [pdfResult, documentsResult] = await Promise.allSettled([
          isParentSelfSelected
            ? httpcommon.get("school/student/pdf-report", {
                params: { preview: 1 },
              })
            : httpcommon.get(
                `school/student/pdf-report/${selectedChild.student_id}/`,
                { params: { preview: 1 } },
              ),
          isParentSelfSelected
            ? httpcommon.get("school/student/documents/")
            : httpcommon.get("school/student/documents/", {
                params: { student_user_id: selectedChild.id },
              }),
        ]);

        const pdfReport =
          pdfResult.status === "fulfilled"
            ? pdfResult.value?.data?.pdf_report || null
            : null;
        const childCareerInfo =
          documentsResult.status === "fulfilled"
            ? documentsResult.value?.data?.careers?.[0]?.career_info || {}
            : {};

        setCareerPdfData(
          pdfReport
            ? { pdf_report: pdfReport, career_info: childCareerInfo }
            : null,
        );
        setCareerInfoData(childCareerInfo);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!userId) return;

    try {
      setLoading(true);

      const csvRes = await httpcommon.get(
        `/school/download_feedback_binary_csv/${userId}/`
      );

      const filePath = csvRes?.data?.file_path;
      if (!filePath) return;

      const [pdfRes, careerRes] = await Promise.all([
        httpcommon.get("school/student/pdf-report", {
          params: { preview: 1 },
        }),
        httpcommon.post("school/generate-career-info/", {
          csv_path: filePath,
        }),
      ]);

      setCareerPdfData({
        pdf_report: pdfRes?.data?.pdf_report || {},
        career_info: careerRes?.data?.career_info || {},
      });
    } catch {
      setCareerPdfData(null);
    } finally {
      setLoading(false);
    }
  }, [isParentPortal, isParentSelfSelected, selectedChild, userId]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);
    // ✅ ADDED
  useEffect(() => {
    const fetchDocuments = async () => {
      if (isParentPortal) return;

      try {
        const response = await httpcommon.get("school/student/documents/");
        if (response.status === 200) {
          setCareerInfoData(response.data?.careers?.[0]?.career_info || {}); // Set initial preview data
        }
      } catch {
        setCareerInfoData({});
      }
    };
    fetchDocuments();
  }, [isParentPortal]);


  const bigResults = useMemo(() => {
    const results = careerPdfData?.pdf_report?.big_results;
    if (!results) return [];
    return Object.entries(results).map(([label, value]) => ({
      label,
      value,
      color: getColorByValue(value),
    }));
  }, [careerPdfData]);

  const personalityCircleData = useMemo(() => {
    const data = careerPdfData?.pdf_report?.personality_test_result;
    if (!data) return [];

    return data.map((item, i) => {
      const topPercent = item.left_percentage;

      let topColor;
      let bottomColor;

      if (i === 0 || i === 2) {
        // Bright Orange / Bright Red
        topColor = "#FCAF2B";
        bottomColor = "#EB4E5F";
      } else {
        // Bright Teal / Bright Purple
        topColor = "#2FAE9B";
        bottomColor = "#6C63FF";
      }

      if (topPercent === 0 || topPercent === 100) {
        topColor = bottomColor =
          topPercent === 0 ? bottomColor : topColor;
      }

      return {
        topLabel: `${item.left_label} ${item.left_description || ""}`.trim(),
        bottomLabel: `${item.right_label} ${item.right_description || ""}`.trim(),
        topPercent,
        topColor,
        bottomColor,
      };
    });
  }, [careerPdfData]);

  const hasCareerInfo = Array.isArray(careerInfoData)
    ? careerInfoData.length > 0
    : Boolean(careerInfoData && Object.keys(careerInfoData).length);
  const hasDashboardData = Boolean(careerPdfData) || hasCareerInfo;

  return (
    <LoggedInLayout searchBar={false}>
      <Box sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        width: "100%",
        overflowX: "hidden",
      }}>
        <Box sx={{
          width: { xs: "100%", md: "calc(100% - 280px)" },
          minWidth: 0,
        }}>


          <Box sx={{
            mt: { xs: "20px", sm: "38px" },
            mb: "20px",
            ml: { xs: "12px", sm: "21px" },
            mr: { xs: "12px", sm: 0 },
            display: "flex",
          }}>
            <CommonTextField
              placeholder="Search"
              name="Search"
              type="Search"
              disabled={true}

              value={""}
              onChange={() => { }}
              inputProps={{

                sx: { backgroundColor: "#F6AA48", padding: "10px 14px", border: "none", color: "#fff" },

              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment sx={{ backgroundColor: "RGB(41 46 92)" }} position="start">
                      {<Search />}
                    </InputAdornment>
                  )
                },
              }}
              sx={{
                width: { xs: "100%", sm: "95%" },
                minWidth: { xs: 0, sm: 400 },
                '& .MuiInputAdornment-root': {
                  maxHeight: '48px',
                  height: '48px',
                  borderTopRightRadius: "8px",
                  borderBottomRightRadius: "8px",
                  backgroundColor: 'RGB(41 46 92)',
                  color: '#fff',
                  width: "50px",
                  marginRight: 0,
                  display: 'flex',
                  justifyContent: 'center',

                },


                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#F5F5F5',
                  height: '48px',
                  paddingRight: '0px',

                  '& fieldset': {
                    border: 'none', // 🔥 remove border
                  },
                  '&:hover fieldset': {
                    border: 'none',
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none',

                  },
                },

                '& .MuiOutlinedInput-input': {
                  padding: '12px 14px',
                  borderTopLeftRadius: "8px",
                  borderBottomLeftRadius: "8px",
                },

              }}
            />
          </Box>
              <Box sx={styles.headerRow}>
              <Box sx={styles.headerBox}>
                <IconButton
                  size="small"
                  sx={{
                    backgroundColor: "rgba(247, 170, 71, 1)",
                    color: "#fff",
                    "&:hover": { backgroundColor: "rgba(247, 170, 71, 1)" },
                  }}
                >
                  <AssignmentInd fontSize="small" />
                </IconButton>
                Start Your Free Career Assessment
              </Box>

              {isParentPortal && (
                <FormControl
                  size="small"
                  sx={{
                    minWidth: { xs: "100%", sm: 240 },
                    maxWidth: { xs: "100%", sm: 320 },
                    bgcolor: "#fff",
                    borderRadius: "10px",
                    boxShadow: "0 8px 18px rgba(41, 46, 92, 0.08)",
                    "& .MuiOutlinedInput-root": {
                      minHeight: 46,
                      borderRadius: "10px",
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#2D315F",
                      "& fieldset": { borderColor: "#E5E8F0" },
                      "&:hover fieldset": { borderColor: "#F6AA48" },
                      "&.Mui-focused fieldset": { borderColor: "#F6AA48" },
                    },
                  }}
                >
                  <Select
                    value={selectedDashboardUserId}
                    onChange={handleDashboardSelectionChange}
                    disabled={childrenLoading}
                    renderValue={(value) => {
                      if (childrenLoading) return "Loading profiles...";
                      if (value === SELF_DASHBOARD_VALUE) {
                        return `${parentDisplayName} (Self)`;
                      }
                      const child = linkedChildren.find(
                        (child) => String(child.id) === String(value),
                      );
                      return child?.name || "Select profile";
                    }}
                  >
                    <MenuItem value={SELF_DASHBOARD_VALUE}>
                      <Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                          {parentDisplayName}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "#7B8498" }}>
                          Self
                        </Typography>
                      </Box>
                    </MenuItem>
                    {linkedChildren.map((child) => (
                      <MenuItem key={child.id} value={String(child.id)}>
                        <Box>
                          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                            {child.name || "Student"}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: "#7B8498" }}>
                            {child.email}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                    {linkedChildren.length === 0 && (
                      <MenuItem value="" disabled>
                        No linked students
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              )}

              <img
                onClick={() =>
                  isParentPortal ? openParentAssessment() : navigate("/assessment")
                }
                src="/assets/newImages/startHere.png"
                alt="Dashboard Header"
                style={{ width: 131, height: 46, cursor: "pointer" }}
              />
            </Box>

            {isParentPortal && selectedChild && (
              <Box
                sx={{
                  mx: { xs: "12px", sm: "21px" },
                  mb: 3,
                  p: { xs: 2, sm: 2.5 },
                  borderRadius: "12px",
                  backgroundColor: "#FFFFFF",
                  border: "1px solid rgba(41, 45, 91, 0.08)",
                  boxShadow: "0 12px 30px rgba(41, 45, 91, 0.08)",
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "flex-start", sm: "center" },
                  justifyContent: "space-between",
                  gap: 1.5,
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#292D5B" }}>
                    Assessment feedback
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "#697089", mt: 0.4 }}>
                    {parentFeedbackLoading
                      ? "Checking feedback status..."
                      : parentFeedbackStatus?.parent_feedback_taken
                        ? "Feedback is submitted. The student can generate the report."
                        : parentFeedbackStatus?.request_pending
                          ? "Parent/Teacher feedback is pending for the selected student."
                          : "No current feedback request for the selected student."}
                  </Typography>
                </Box>

                <Box
                  onClick={openParentAssessment}
                  sx={{
                    backgroundColor: parentFeedbackStatus?.parent_feedback_taken
                      ? "#2E7D32"
                      : parentFeedbackStatus?.request_pending
                        ? "#F6AA48"
                        : "#292D5B",
                    color: "#fff",
                    fontSize: "13px",
                    fontWeight: 800,
                    px: 2,
                    py: 1,
                    borderRadius: "8px",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  {parentFeedbackStatus?.parent_feedback_taken
                    ? "View Status"
                    : parentFeedbackStatus?.request_pending
                      ? "Fill Feedback"
                      : "View"}
                </Box>
              </Box>
            )}

            {!hasDashboardData && !loading ? (
              <Box sx={{ m: "20px", display: "flex", flexDirection: "column", gap: "20px" }}>
                <Box component="img" src="/assets/newImages/Banner_no_button.jpg" alt="Assessment overview" sx={{ width: "100%" }} />
                <Box component="img" src="/assets/newImages/career-ahead-bg.jpg" alt="Career guidance" sx={{ width: "100%" }} />
                <Box component="img" src="/assets/newImages/noassessmentbanner.jpg" alt="No assessment report" sx={{ width: "100%" }} />
              </Box>
            ) : (
              <Box sx={styles.wrapper}>
                <Box sx={styles.personalitySection}>
                  {loading ? (
                    <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mt: 4, mb: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : personalityCircleData.length > 0 ? (
                    personalityCircleData.map((item, i) => (
                      <PersonalityCircle key={i} {...item} />
                    ))
                  ) : (
                    <Typography color="text.secondary">
                      Personality results are not available.
                    </Typography>
                  )}
                </Box>

                <Box sx={styles.progressSection}>
                  <Box sx={styles.headerBox}>
                    <IconButton
                      size="large"
                      sx={{
                        padding: "15px",
                        backgroundColor: "rgba(153, 211, 219, 1)",
                        "&:hover": { backgroundColor: "rgba(153, 211, 219, 1)" },
                      }}
                    />
                    Aptitude Graph from Last Test Appeared
                  </Box>

                  {loading ? (
                    <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mt: 6, mb: 6 }}>
                      <CircularProgress />
                    </Box>
                  ) : bigResults.length > 0 ? (
                    <Box mt={5} sx={{ width: "90%" }}>
                      {bigResults.map((item, index) => (
                        <ProgressBar key={index} {...item} />
                      ))}
                    </Box>
                  ) : (
                    <Typography color="text.secondary" mt={4}>
                      Aptitude results are not available.
                    </Typography>
                  )}
                </Box>

                <Box>
                  <RecommendedCareer showMilestoneLayout={true} career={careerInfoData || {}} />
                </Box>
              </Box>
            )}
        </Box>
        <Box sx={{
          width: { xs: "100%", md: 280 },
          flexShrink: 0,
        }}>
          <JourenyStepper></JourenyStepper>
        </Box>

      </Box>

    </LoggedInLayout>
  );
};

export default Dashboard;
